import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowRight, X } from "lucide-react";
import { Img, Reveal } from "@/components/site/Motion";
import { Spec } from "@/components/site/Section";
import { useEnquiry } from "@/components/site/EnquiryContext";

export const useReturnFocus = (open) => {
  const ref = useRef(null);
  useEffect(() => {
    if (open && document.activeElement instanceof HTMLElement && document.activeElement !== document.body) ref.current = document.activeElement;
  }, [open]);
  return (e) => {
    const el = ref.current;
    if (el && document.contains(el)) { e.preventDefault(); el.focus({ preventScroll: true }); }
  };
};

export const InfoModal = ({ open, onOpenChange, title, eyebrow, children, testid }) => {
  const onCloseAutoFocus = useReturnFocus(open);
  return (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Portal>
      <Dialog.Overlay className="modal-overlay fixed inset-0 z-[80] bg-[#0B1C2D]/70" />
      <Dialog.Content onCloseAutoFocus={onCloseAutoFocus} className="modal-panel modal-wide fixed z-[90] flex flex-col bg-white text-[#151A20]" data-testid={testid} aria-describedby={undefined}>
        <div className="modal-head flex items-start justify-between gap-4 border-b border-[#E2E8EE] bg-white px-6 pb-4 pt-6 md:px-8 md:pt-7">
          <div>
            {eyebrow && <p className="eyebrow-dark">{eyebrow}</p>}
            <Dialog.Title className="mt-1 text-xl font-bold text-[#102A43] md:text-2xl">{title}</Dialog.Title>
          </div>
          <Dialog.Close className="btn-glass btn-icon shrink-0" aria-label="Close" data-testid={`${testid}-close-btn`}><X aria-hidden="true" size={20} /></Dialog.Close>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-8 md:px-8">{children}</div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
  );
};

const Block = ({ title, children }) => (
  <div className="border-t border-[#D9E1E8] pt-6 first:border-t-0 first:pt-0">
    <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-[#3E5468]">{title}</h3>
    <div className="mt-3">{children}</div>
  </div>
);

const useHandoff = (setSelected) => {
  const { openEnquiry } = useEnquiry();
  return (item, section, opts) => {
    setSelected(null);
    setTimeout(() => openEnquiry(section, { ...opts, onReturn: () => setSelected(item) }), 180);
  };
};

const SystemCard = ({ s, onOpen }) => {
  const image = s.photo || s.tech;
  return (
    <article id={`system-${s.id}`} className="card flex h-full scroll-mt-24 flex-col overflow-hidden" data-testid={`system-card-${s.id}`}>
      <div className={`aspect-[4/3] overflow-hidden ${s.photo ? "" : "bg-white p-6"}`}>
        <Img {...image} sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" className={`h-full w-full ${s.photo ? "object-cover" : "object-contain"}`} />
      </div>
      <div className="flex flex-1 flex-col border-t border-[#E2E8EE] p-6 md:p-7">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#3E5468]">{s.series}</p>
        <h3 className="mt-2 text-xl font-bold text-[#102A43]">{s.name}</h3>
        <p className="mt-3 flex-1 text-base leading-relaxed text-[#151A20]">{s.short}</p>
        <p className="mt-4 text-sm text-[#2B3642]"><span className="font-semibold text-[#151A20]">Typical application:</span> {s.application}</p>
        <button type="button" className="btn-glass btn-secondary mt-6 self-start" onClick={() => onOpen(s)} data-testid={`system-learn-more-${s.id}`}>Learn more <ArrowRight aria-hidden="true" size={16} /></button>
      </div>
    </article>
  );
};

export const SystemGrid = ({ systems, category, page }) => {
  const [selected, setSelected] = useState(null);
  const handoff = useHandoff(setSelected);
  const s = selected;
  return (
    <>
      <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {systems.map((x, i) => <li key={x.id}><Reveal delay={(i % 3) * 0.06} className="h-full"><SystemCard s={x} onOpen={setSelected} /></Reveal></li>)}
      </ul>
      <InfoModal open={!!s} onOpenChange={(v) => { if (!v) setSelected(null); }} title={s?.name} eyebrow={s?.series} testid="system-modal">
        {s && (
          <div className="grid gap-8">
            <div className={`grid gap-4 ${s.photo && s.tech ? "sm:grid-cols-[1.6fr_1fr]" : ""}`}>
              {s.photo && <div className="img-frame aspect-[4/3]"><Img {...s.photo} priority sizes="(min-width: 768px) 520px, 100vw" className="h-full w-full object-cover" /></div>}
              {s.tech && <div className="flex items-center justify-center rounded-[10px] border border-[#E2E8EE] bg-white p-4"><Img {...s.tech} priority sizes="320px" className="max-h-[300px] w-auto object-contain" /></div>}
            </div>
            {s.photo && <p className="-mt-4 text-xs text-[#4A5866]">Photo: system example. {s.tech ? "Drawing: profile illustration." : ""}</p>}
            <Block title="What it is"><p className="text-base leading-relaxed">{s.what}</p></Block>
            <Block title="Suitable applications"><Spec items={s.applications} /></Block>
            <Block title="Main benefits"><Spec items={s.benefits} /></Block>
            <Block title="Configurations and technical details"><Spec items={[...s.specs, ...(s.tech2 ? [] : [])]} />{s.tech2 && <div className="mt-5 inline-block rounded-[10px] border border-[#E2E8EE] p-3"><Img {...s.tech2} sizes="200px" className="h-48 w-auto object-contain" /></div>}</Block>
            <div className="border-t border-[#D9E1E8] pt-6">
              <button type="button" className="btn-glass" onClick={() => handoff(s, `${page} systems — ${s.name} — Ask about this system`, { category, context: `${s.name} (${s.series})`, prefill: `I'd like to ask about ${s.name} (${s.series}). ` })} data-testid="system-modal-ask-btn">Ask about this system</button>
            </div>
          </div>
        )}
      </InfoModal>
    </>
  );
};

export const ProfileCards = ({ profiles }) => {
  const [selected, setSelected] = useState(null);
  const handoff = useHandoff(setSelected);
  const p = selected;
  return (
    <>
      <ul className="grid gap-3 sm:gap-6 lg:grid-cols-3">
        {profiles.map((x, i) => (
          <li key={x.id}>
            <Reveal delay={i * 0.06} className="card flex h-full flex-col p-4 sm:p-7 md:p-9" >
              <p className="hidden text-sm font-semibold tabular-nums text-[#7890A5] sm:block">{String(i + 1).padStart(2, "0")}</p>
              <div className="flex items-start justify-between gap-3 sm:block">
                <h3 className="text-[17px] font-bold leading-snug text-[#102A43] sm:mt-4 sm:text-2xl">{x.label}</h3>
                <span className="shrink-0 sm:hidden"><button type="button" className="btn-glass btn-secondary btn-compact" onClick={() => setSelected(x)} aria-label={`Learn more: ${x.label}`} data-testid={`profile-learn-more-mobile-${x.id}`}>Learn more</button></span>
              </div>
              <p className="mt-1.5 flex-1 text-sm leading-snug text-[#151A20] sm:mt-4 sm:text-base sm:leading-relaxed">{x.card}</p>
              <div className="mt-8 hidden sm:block"><button type="button" className="btn-glass btn-secondary" onClick={() => setSelected(x)} data-testid={`profile-learn-more-${x.id}`}>Learn more <ArrowRight aria-hidden="true" size={16} /></button></div>
            </Reveal>
          </li>
        ))}
      </ul>
      <InfoModal open={!!p} onOpenChange={(v) => { if (!v) setSelected(null); }} title={p ? `Are you ${p.label.charAt(0).toLowerCase()}${p.label.slice(1)}` : ""} eyebrow="Working with Alumicom" testid="profile-modal">
        {p && (
          <div className="grid gap-8">
            <Block title="What you're trying to achieve"><p className="text-base leading-relaxed">{p.goal}</p></Block>
            <Block title="Common concerns"><Spec items={p.concerns} /></Block>
            <Block title="How Alumicom helps"><Spec items={p.help} /></Block>
            <Block title="Relevant systems and services"><Spec items={p.systems} /></Block>
            <Block title="What happens when you enquire"><ol className="grid gap-2.5 text-[15px]">{p.next.map((t, i) => <li key={t} className="flex gap-3"><span className="w-5 shrink-0 font-semibold text-[#3E5468]">{i + 1}.</span>{t}</li>)}</ol></Block>
            <div className="border-t border-[#D9E1E8] pt-6">
              <button type="button" className="btn-glass" onClick={() => handoff(p, `Home "Are you" — ${p.id} — ${p.cta}`, { category: p.category, context: `Profile: ${p.label.replace(/^An? |\?$/g, "")}` })} data-testid="profile-modal-cta-btn">{p.cta}</button>
            </div>
          </div>
        )}
      </InfoModal>
    </>
  );
};
